/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Componente: IssueAttachmentsCard
 * ----------------------------------------------------------------------------
 * O que faz:
 * - Lista anexos de uma ocorrência (issue) e permite enviar novos arquivos.
 * - Endpoint upload:   POST /api/partners/issue-attachments/  (multipart)
 *   Campos: issue, file, description?
 * - Listagem por issue: GET  /api/partners/issue-attachments/?issue=<id>
 * - Atualiza automaticamente após upload.
 * - Visual neutro, com bom contraste (dark first).
 * ========================================================================== */

import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

type Attachment = {
    id: number;
    issue: number;
    file: string;           // URL retornada pelo backend
    description?: string | null;
    created_at?: string | null;
    created_by_name?: string | null; // se o backend fornecer
};

type ListResponse<T> = { results?: T[] } | T[];

function asArray<T>(data: ListResponse<T> | undefined): T[] {
    if (!data) return [];
    return Array.isArray(data) ? data : Array.isArray(data.results) ? data.results! : [];
}

function filenameFromUrl(url: string) {
    try {
        const u = new URL(url, window.location.origin);
        const last = u.pathname.split("/").filter(Boolean).pop();
        return last || url;
    } catch {
        const parts = url.split("?")[0].split("/");
        return parts[parts.length - 1] || url;
    }
}

function isImage(url: string) {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split("?")[0]);
}

export default function IssueAttachmentsCard({
    issueId,
    count,
    className = "",
}: {
    issueId: number;
    /** opcional: contador vindo da lista (attachments_count) só para exibir */
    count?: number;
    className?: string;
}) {
    const qc = useQueryClient();

    const q = useQuery({
        queryKey: ["supplier-issue-attachments", issueId],
        queryFn: async () => {
            const { data } = await api.get<ListResponse<Attachment>>(
                `/partners/issue-attachments/?issue=${issueId}&page_size=200`
            );
            return asArray<Attachment>(data);
        },
        staleTime: 15_000,
        enabled: Number.isFinite(issueId),
    });

    const [desc, setDesc] = useState("");
    const fileRef = useRef<HTMLInputElement | null>(null);

    const m = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append("issue", String(issueId));
            fd.append("file", file);
            if (desc.trim()) fd.append("description", desc.trim());
            // endpoint de upload (multipart)
            const { data } = await api.post("/partners/issue-attachments/", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            setDesc("");
            if (fileRef.current) fileRef.current.value = "";
            qc.invalidateQueries({ queryKey: ["supplier-issue-attachments", issueId] });
        },
    });

    const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) m.mutate(f);
    };

    const total = useMemo(() => q.data?.length ?? 0, [q.data]);
    const headerCount = typeof count === "number" ? count : total;

    return (
        <div
            className={[
                "rounded-xl border p-4",
                "border-neutral-800 bg-neutral-900 text-neutral-100",
                className,
            ].join(" ")}
        >
            <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-base font-semibold">Anexos</h3>
                    <p className="text-xs text-neutral-400">
                        {headerCount} arquivo(s){total !== headerCount ? ` • local: ${total}` : ""}
                    </p>
                </div>
                <label className="inline-flex items-center gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        className="file-input file-input-sm file-input-bordered bg-neutral-800 border-neutral-700 text-neutral-100"
                        onChange={onPickFile}
                        disabled={m.isPending}
                    />
                </label>
            </div>

            <div className="mb-3">
                <input
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="Descrição (opcional)"
                    className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm outline-none"
                />
                {m.isPending && (
                    <div className="mt-1 text-xs text-neutral-400">Enviando…</div>
                )}
                {m.isError && (
                    <div className="mt-1 text-xs text-red-400">
                        Falha ao enviar arquivo. Tente novamente.
                    </div>
                )}
            </div>

            <div className="max-h-64 overflow-auto rounded-md border border-neutral-800">
                {q.isLoading ? (
                    <div className="p-3 text-sm text-neutral-400">Carregando anexos…</div>
                ) : (q.data?.length ?? 0) === 0 ? (
                    <div className="p-3 text-sm text-neutral-400">Nenhum anexo.</div>
                ) : (
                    <ul className="divide-y divide-neutral-800">
                        {q.data!.map((att) => (
                            <li key={att.id} className="flex items-center gap-3 p-3">
                                {/* Preview pequeno se for imagem */}
                                {isImage(att.file) ? (
                                    <a href={att.file} target="_blank" rel="noreferrer">
                                        <img
                                            src={att.file}
                                            alt="preview"
                                            className="h-10 w-10 rounded object-cover border border-neutral-700"
                                        />
                                    </a>
                                ) : (
                                    <div className="h-10 w-10 rounded bg-neutral-800 flex items-center justify-center text-xs border border-neutral-700">
                                        ARQ
                                    </div>
                                )}

                                <div className="min-w-0 flex-1">
                                    <a
                                        href={att.file}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block truncate text-sm font-medium hover:underline"
                                        title={att.file}
                                    >
                                        {filenameFromUrl(att.file)}
                                    </a>
                                    <div className="text-xs text-neutral-400 truncate">
                                        {att.description || "—"}
                                    </div>
                                </div>

                                {att.created_at && (
                                    <div className="text-[11px] text-neutral-500 whitespace-nowrap">
                                        {new Date(att.created_at).toLocaleString()}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
